import { useState, useEffect, useRef } from 'react';
import { fetchCustomRewards } from '../services/TwitchRewardService';
import { storage } from '../utils/storage';
import { STORAGE_KEYS } from '../config/constants';

export function useRewards({
  twitchUser,
  selectedRewardId,
  setSelectedRewardId,
}) {
  const [rewards, setRewards] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const isInitializedRef = useRef(false);
  const loadingRef = useRef(false);
  const twitchUserIdRef = useRef(null);

  useEffect(() => {
    async function loadRewards() {
      // Vérifier que l'utilisateur Twitch est connecté
      if (!twitchUser) {
        twitchUserIdRef.current = null;
        return;
      }

      // Le token JWT est maintenant géré via cookies httpOnly
      // Plus besoin de vérifier localStorage.getItem('userToken')

      // Éviter les appels multiples si déjà en cours ou si le même utilisateur
      const currentUserId = twitchUser.id || twitchUser.user_id;
      if (loadingRef.current || twitchUserIdRef.current === currentUserId) {
        return;
      }

      twitchUserIdRef.current = currentUserId;
      loadingRef.current = true;
      setLoading(true);
      setError(null);

      try {
        // Récupérer toutes les récompenses et celles gérables en parallèle
        const [allRewards, manageableRewards] = await Promise.all([
          fetchCustomRewards(),
          fetchCustomRewards({ onlyManageable: true }),
        ]);

        // Créer un Set des IDs des récompenses gérables pour une recherche rapide
        const manageableIds = new Set(manageableRewards.map((r) => r.id));

        // Marquer chaque récompense comme gérable ou non
        const rewardsData = allRewards.map((reward) => ({
          ...reward,
          is_manageable: manageableIds.has(reward.id),
        }));

        setRewards(rewardsData);

        // Gestion sélection rewardId dans localStorage + state (seulement au premier chargement)
        if (
          !isInitializedRef.current &&
          !selectedRewardId &&
          rewardsData.length > 0
        ) {
          const savedRewardId = storage.getString(
            STORAGE_KEYS.SELECTED_REWARD_ID
          );
          if (
            savedRewardId &&
            rewardsData.some((r) => r.id === savedRewardId)
          ) {
            setSelectedRewardId(savedRewardId);
          } else {
            setSelectedRewardId(rewardsData[0].id);
          }
          isInitializedRef.current = true;
        }
      } catch (err) {
        // Gérer les erreurs d'authentification
        if (
          err.message &&
          (err.message.includes('Non authentifié') ||
            err.message.includes('Authentification Twitch requise'))
        ) {
          // Ne pas afficher d'erreur : le contenu est flouté avec un message invitant à s'authentifier
          setError(null);
        } else {
          setError(err.message);
          console.error('Erreur lors du chargement des récompenses:', err);
        }
      } finally {
        setLoading(false);
        loadingRef.current = false;
      }
    }
    loadRewards();
    // Retirer setSelectedRewardId des dépendances car c'est une fonction setState stable
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [twitchUser]);

  useEffect(() => {
    if (selectedRewardId) {
      storage.setString(STORAGE_KEYS.SELECTED_REWARD_ID, selectedRewardId);
    }
  }, [selectedRewardId]);

  return { rewards, loading, error, setRewards };
}
